import { Document, Page, Text, View } from "@react-pdf/renderer";
import type { ReactElement } from "react";
import type { DocumentProps } from "@react-pdf/renderer";
import type { ResumeDocument } from "@/types/cv";
import { normalizeRewriteResumeSections } from "../rewrite-section-normalizer";
import { formatMonthYear } from "./utils";
import {
  INLINE_SEPARATOR,
  PAGE_SIZE,
  createPdfStyles,
  resolvePdfTemplateOptions,
  type ResumePdfTemplateOptions,
  type ResolvedPdfTemplateOptions,
} from "./pdf-theme";
import {
  BulletRow,
  EducationSection,
  SECTION_HEADING_KEEP_AHEAD,
  TimelineEntryView,
  collapseMultiline,
  resolveDisplayedTitle,
  type TimelineEntry,
} from "./pdf-sections";

export type { ResumePdfTemplateOptions } from "./pdf-theme";

const buildSkillItems = (
  resume: ResumeDocument,
  theme: ResolvedPdfTemplateOptions,
): string[] => {
  if (!theme.includeHighlights) return [];

  const additional = resume.additional_information;
  const joinList = (values: string[]) =>
    values
      .map((value) => value.trim())
      .filter(Boolean)
      .join(", ");

  const items: string[] = [];

  const technicalSkills = joinList(additional.technical_skills);
  if (technicalSkills) items.push(`Technical Skills: ${technicalSkills}`);

  const tools = joinList(additional.tools);
  if (tools) items.push(`Tools: ${tools}`);

  const softSkills = joinList(additional.soft_skills);
  if (softSkills) items.push(`Soft Skills: ${softSkills}`);

  const languages = additional.languages
    .map((language) => {
      const languageName = language.name.trim();
      if (!languageName) return "";
      const proficiency = language.proficiency.trim();
      return proficiency ? `${languageName} (${proficiency})` : languageName;
    })
    .filter(Boolean)
    .join(", ");
  if (languages) items.push(`Languages: ${languages}`);

  additional.certificates_awards.forEach((certificate) => {
    const { displayTitle } = resolveDisplayedTitle(
      certificate.title,
      certificate.rewrite_title,
    );
    if (!displayTitle) return;

    const date = formatMonthYear(certificate.date);
    const meta = [certificate.issuer.trim(), date]
      .filter(Boolean)
      .join(INLINE_SEPARATOR);
    items.push(meta ? `${displayTitle}: ${meta}` : displayTitle);
  });

  return items;
};

export function ResumePdfTemplateDocument(props: {
  resume: ResumeDocument;
  options: ResumePdfTemplateOptions;
}): ReactElement<DocumentProps> {
  const theme = resolvePdfTemplateOptions(props.options);
  const styles = createPdfStyles(theme);
  const resume = normalizeRewriteResumeSections(props.resume) ?? props.resume;

  const SectionTitle = ({ title }: { title: string }) => (
    <View minPresenceAhead={SECTION_HEADING_KEEP_AHEAD}>
      <Text style={styles.sectionHeading}>
        {theme.headingTransform === "uppercase" ? title.toUpperCase() : title}
      </Text>
    </View>
  );

  const identity = resume.personal_identity;
  const name = identity.name.trim();
  const location = [identity.location.city.trim(), identity.location.country.trim()]
    .filter((value) => value.length > 0)
    .join(", ");
  const contact = [
    location,
    identity.email.trim(),
    identity.phone_number.trim(),
    identity.linkedin.trim(),
  ]
    .filter((value) => value.length > 0)
    .join(theme.contactSeparator);
  const additionalLinks = identity.additional_links
    .map((link) => {
      const label = link.label.trim();
      const url = link.url.trim();
      if (label && url) return `${label}: ${url}`;
      return url || label;
    })
    .filter((value) => value.length > 0)
    .join(theme.contactSeparator);

  const summary = collapseMultiline(resume.professional_summary);

  const toTimelineEntry = (entry: {
    title: string;
    rewrite_title: string;
    entityName: string;
    metaParts: string[];
    start_date: string | null;
    end_date: string | null;
    is_current: boolean;
    experience_detail: string;
    points: string[];
  }): TimelineEntry => {
    const { displayTitle, originalTitle } = resolveDisplayedTitle(
      entry.title,
      entry.rewrite_title,
    );
    return {
      title: displayTitle,
      originalTitle,
      entityName: entry.entityName,
      metaParts: entry.metaParts,
      startDate: entry.start_date,
      endDate: entry.end_date,
      isCurrent: entry.is_current,
      detail: entry.experience_detail,
      points: entry.points,
    };
  };

  const timelineSections: { title: string; entries: TimelineEntry[] }[] = [
    {
      title: "Experience",
      entries: resume.working_experience.map((experience) =>
        toTimelineEntry({
          ...experience,
          entityName: experience.company,
          metaParts: [experience.location, experience.employment_type],
        }),
      ),
    },
    ...(theme.includeOrganizations
      ? [
          {
            title: "Organizations",
            entries: resume.organizations.map((organization) =>
              toTimelineEntry({
                ...organization,
                entityName: organization.org,
                metaParts: [organization.location],
              }),
            ),
          },
      ]
      : []),
  ].filter((section) => section.entries.length > 0);

  const skillItems = buildSkillItems(resume, theme);

  const hasContent = Boolean(
    name ||
      contact ||
      additionalLinks ||
      summary ||
      timelineSections.length > 0 ||
      resume.education.length > 0 ||
      skillItems.length > 0,
  );

  return (
    // Metadata mirrors the DOCX creator/description in docx-builder.ts.
    <Document
      title={`Resume - ${name || "Document"}`}
      author={name || "Hyred"}
      subject="Hyred rewritten resume"
      creator="Hyred"
      producer="Hyred"
    >
      <Page size={PAGE_SIZE} style={styles.page}>
        {!hasContent ? (
          <Text>Resume content is not available.</Text>
        ) : (
          <View>
            <View wrap={false}>
              {name ? <Text style={styles.headerName}>{name}</Text> : null}
              {contact ? (
                <Text style={styles.headerContact}>{contact}</Text>
              ) : null}
              {additionalLinks ? (
                <Text style={styles.headerLinks}>{additionalLinks}</Text>
              ) : null}
            </View>

            {summary ? (
              <View>
                <SectionTitle title="Summary" />
                <Text style={styles.summaryText} orphans={2} widows={2}>
                  {summary}
                </Text>
              </View>
            ) : null}

            {timelineSections.map((section) => (
              <View key={section.title}>
                <SectionTitle title={section.title} />
                {section.entries.map((entry, index) => (
                  <TimelineEntryView key={index} styles={styles} entry={entry} />
                ))}
              </View>
            ))}

            <EducationSection
              styles={styles}
              resume={resume}
              sectionTitle={<SectionTitle title="Education" />}
            />

            {skillItems.length > 0 ? (
              <View>
                <SectionTitle title="Skills" />
                {skillItems.map((item, index) => (
                  <BulletRow key={index} styles={styles} text={item} />
                ))}
              </View>
            ) : null}
          </View>
        )}
      </Page>
    </Document>
  );
}
